import axios from 'axios';
import { postForm } from '@/shared/api/client';
import { submitForm } from '@/shared/api/submit';

export const deleteAiQuery = (id: number) => submitForm('/ai/request-cache/delete/', { id });

interface RawAiRequest {
  id: number;
  message_from_user?: string;
  additional_queries?: { query: string }[];
  car: { id_mark?: number; id_model?: number; id_generation?: number; id_modification?: number };
  service_catalog?: { id: number }[];
  sum_services_labor_hours?: number | string;
}

export interface AiRequest {
  id: number;
  message: string;
  normHours: number;
}

export interface AiRequestValues {
  variantsMessage: string[];
  markId: string;
  modelId: string;
  generationId: string;
  modificationId: string;
  serviceIds: number[];
}

const toId = (id?: number) => (id ? String(id) : '');

export async function fetchAiRequest(id: number) {
  const raw = await postForm<RawAiRequest>('/ai/request-cache/view/', { id });
  const request: AiRequest = {
    id: raw.id,
    message: raw.message_from_user ?? '',
    normHours: Number(raw.sum_services_labor_hours) || 0,
  };
  const values: AiRequestValues = {
    variantsMessage: (raw.additional_queries ?? []).map((item) => item.query),
    markId: toId(raw.car.id_mark),
    modelId: toId(raw.car.id_model),
    generationId: toId(raw.car.id_generation),
    modificationId: toId(raw.car.id_modification),
    serviceIds: (raw.service_catalog ?? []).map((service) => service.id),
  };
  return { request, values };
}

export const saveAiRequest = (id: number, values: AiRequestValues) =>
  submitForm('/ai/request-cache/edit/', {
    id,
    id_modification: values.modificationId,
    service_catalog: values.serviceIds,
    additional_queries: values.variantsMessage,
  });

export interface ServiceCategory {
  id: number;
  name: string;
  children: { id: number; name: string }[];
}

interface WorkTypeNode {
  id: number;
  name: string;
  level: number;
  parent_id: number | null;
  sort_id?: number;
}

const bySort = (a: WorkTypeNode, b: WorkTypeNode) => (a.sort_id ?? 0) - (b.sort_id ?? 0);

/**
 * Root categories with their level-2 works (level-1 groups are flattened away), as in the legacy page.
 *
 * SITE API rejects `admin_auth_token` on this endpoint (403), but allows the call without a token —
 * same as legacy, which accidentally sent an empty cookie value.
 */
export async function fetchServiceCategories(): Promise<ServiceCategory[]> {
  const { data } = await axios.post<{
    success?: number;
    data?: { work_types?: WorkTypeNode[] };
  }>(`${__CONFIG__.SITE_API_URL}/v2/work_type/get-tree-with-selected/`, new FormData());

  if (!data?.success) {
    throw new Error('Не удалось загрузить услуги');
  }

  const nodes = data.data?.work_types ?? [];
  const groupsOf = (rootId: number) => nodes.filter((n) => n.level === 1 && n.parent_id === rootId);

  return nodes
    .filter((n) => n.level === 0)
    .map((root) => {
      const groupIds = new Set(groupsOf(root.id).map((group) => group.id));
      const works = nodes.filter((n) => n.level === 2 && n.parent_id !== null && groupIds.has(n.parent_id));
      return { id: root.id, name: root.name, children: works.sort(bySort).map(({ id, name }) => ({ id, name })) };
    });
}

/** Sum of labor hours of the services for the car modification (AI service). */
export async function fetchNormHours(serviceIds: number[], modificationId: string, generationId: string) {
  const hours = await Promise.all(
    serviceIds.map(async (serviceId) => {
      const { data } = await axios.post<{ labor_hours?: number | string }>(`${__CONFIG__.AI_API}/norm-hours/view`, {
        service_id: serviceId,
        id_modification: modificationId,
        id_generation: generationId,
      });
      return Number(data?.labor_hours) || 0;
    }),
  );
  return hours.reduce((sum, value) => sum + value, 0);
}
